import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbErrorBoundary } from "abreact/incubator/ui/component/AbErrorBoundary";
import { ReactErrorRenderer } from "abreact/incubator/ui/error/ReactErrorRenderer";
import { LayerManager } from "abreact/incubator/ui/manager/LayerManager";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbReactServices } from "abreact/service/AbReactServices";
import { AbBusyIndicator, AbBusyIndicatorSize } from "absdk/commons/ui/misc/AbBusyIndicator";
import { AbWebServices } from "abweb/service/AbWebServices";
import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";
import { ModuleLoaderUtils } from "abweb/utils/ModuleLoaderUtils";
import { AlreadyHandledError } from "abweb/utils/PromiseUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import * as React from "react";
import * as ReactDOM from "react-dom";
/**
 * Minimal Bootstrapper that renders a spinner while it is dynamically loading
 * and running the 'startApp' method of your application,
 * and then hands off to app-specific component to render once the app is running.
 * It also sets up the LayerManager and global 'error handler of last resort'.
 */
export class BootstrapApp extends React.Component {
  /** entry point called by application loading workflow */
  static startApp(containingNode, appLoaderFn, errorContextPipeline, profileStartup, absdkDir, headless, portalLoginView) {
    URLUtils.setAbsdkDirPrefix(absdkDir);

    // Launch into react world as quickly as possible
    const props = {
      containingNode,
      appLoaderFn,
      errorContextPipeline,
      profileStartup,
      headless,
      portalLoginView
    };
    ReactDOM.render(/*#__PURE__*/React.createElement(BootstrapApp, props), containingNode);
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "renderCnt", 0);
    _defineProperty(this, "reactErrRenderer", void 0);
    _defineProperty(this, "invokedVeryLastChanceErrorRenderer", false);
    this.tellBrowserToReloadWhenUsingBfcache();
    this.state = {
      renderApp: null,
      busyIndicatorVisible: true,
      busyMsg: "",
      layerRootCreated: false,
      errorContent: undefined,
      portalLoginView: false
    };

    // Do NOT start loading here as updateLoadingHtmlMsg fn (which uses setState)
    // can only be called once app is mounting or mounted
  }

  // PORTAL-1242: Portal throws error if navigating to it using front or back browser buttons from another site
  tellBrowserToReloadWhenUsingBfcache() {
    // Force reload when restored from bfcache (back-forward cache) to re-run initialization.
    // See https://web.dev/bfcache/
    // eslint-disable-next-line abinitio/check-listeners -- intentionally never removed; must persist for bfcache detection
    window.addEventListener("pageshow", event => {
      if (event.persisted) {
        window.location.reload();
      }
    });
  }
  myProfiler(msg) {
    this.props.profileStartup("BootstrapApp: " + msg);
  }
  async componentDidMount() {
    installLoggerClickTestIds();
    if (!this.state.layerRootCreated) {
      if (!this.props.headless) {
        LayerManager.makeCustomLayerGroup(LayerManager.DEFAULT_GROUP_ID, this.props.containingNode);
      }
      this.setState({
        layerRootCreated: true
      });
    }

    // Want localized content, so don't hardcode busy msg.
    // this.updateBusyMsg("Loading application");
    // Instead, ensure that the app loader invokes the <App>ServiceInitializer.afterAbWebServicesInitialized
    // method as soon as AbWebServices are initialized (first point where i18n service is ready for use)

    const appLoaderProps = {
      profileStartup: this.props.profileStartup,
      setBusyIndicatorVisible: this.setBusyIndicatorVisible.bind(this),
      updateBusyMsg: this.updateBusyMsg.bind(this),
      errorContextPipeline: this.props.errorContextPipeline,
      appInitializationErrorHandler: this.handleAppInitializationError.bind(this),
      headless: this.props.headless,
      setErrorContent: this.setErrorContent.bind(this)
    };
    try {
      //This is done IN THE BACKGROUND so that rendering of spinner happens quickly
      const renderApp = await this.props.appLoaderFn(appLoaderProps);
      this.setState({
        renderApp
      }); // Causes rerender
      // Now that app services (including i18nService and ui-related infrastructure) have been initialized,
      // update the error handling to display errors using app-ui infrastructure.  Note if any async errors
      // have already been handled by 'last chance error catcher', they've been logged and buffered, and
      // now that there is a UI for them they will be displayed.
      // Currently ReactErrorRenderer defaults to hardcoded resources (see AbErrorDialog.tsx) if resources aren't initialized,
      // but it has lots of 'machines' that rely on at least i18n being initialized  (and they will throw if not available)
      if (!this.props.headless) {
        this.props.errorContextPipeline.renderHandler = new ReactErrorRenderer();
      }
      this.myProfiler("Finished application startup");
    } catch (err) {
      this.handleAppInitializationError(err);
    }
  }
  componentWillUnmount() {
    if (!this.props.headless) {
      LayerManager.destroy(); // safe if null
    }
    if (this.props.errorContextPipeline) {
      this.props.errorContextPipeline.destroy();
    }
  }
  render() {
    this.myProfiler("Rendering BootstrapApp for the " + ++this.renderCnt + " time.");
    // Note RouteErrorBoundary can safely be called before ANY services are initialized (including i18n)

    let renderContent;
    if (this.state.errorContent) {
      renderContent = this.state.errorContent;
    } else if (this.state.renderApp) {
      renderContent = this.state.renderApp();
    } else {
      renderContent = this.renderBusyIndicator();
    }
    return /*#__PURE__*/React.createElement(AbErrorBoundary, {
      tryAgainOnRouteChange: true
    }, renderContent);
  }
  renderBusyIndicator() {
    const classNames = ReactUtils.classNamesString(["ab-main-app-initialization-indicator", this.props.portalLoginView && "ab-portal-login-background"]);

    // Note busyIndicator is turned off when login dialog is displayed
    return this.state.busyIndicatorVisible ? /*#__PURE__*/React.createElement("div", {
      className: classNames
    }, /*#__PURE__*/React.createElement(AbBusyIndicator, {
      size: AbBusyIndicatorSize.LARGE,
      message: this.state.busyMsg
    })) : null;
  }
  setErrorContent(errorContent) {
    this.setState({
      errorContent
    });
  }
  setBusyIndicatorVisible(visible) {
    this.setState({
      busyIndicatorVisible: visible
    });
  }
  updateBusyMsg(msg) {
    this.setState({
      busyMsg: msg
    });
    // Let's get some free profiling every time they update the loading msg
    this.props.profileStartup("Busy Indicator Msg Updated: " + msg);
  }

  // CAT-1236: try to gracefully handle application initialization errors.  Assumption is that if an exception is
  // thrown during app initialization, then the application can't be trusted and thus conservatively show user the
  // error (w/PFS button if they've gotten far enough during initialization) and then re-load app.
  handleAppInitializationError(err) {
    if (ClassUtils.isInstanceOf(err, AlreadyHandledError)) {
      // If they've already handled it, then we do nothing.
      // Known use case:  CAT-1280 where access denied dialog already displayed error and initiated logout, then
      //   it threw an AlreadyHandledError to skip the rest of app initialization.
      //   In this case, the below logic should be skipped (otherwise see redundant err dialog/logout)
      return;
    }

    // Now add the application initialization error that was thrown from the main thread (aka task queue)
    // AbWebServices and AbReactServices are available
    try {
      this.setBusyIndicatorVisible(true);
      this.updateBusyMsg("Application Initialization Error");

      // From here on out: only concerned with rendering error so that user sees it
      if (!this.isReactErrorRendererUsable()) {
        this.veryLastChanceErrorRenderer(err);
        return;
      }

      // Now add a react error renderer that will display all buffered errors,
      // (including those thrown from promise chains aka micro task queue during initialization)
      // and when closed will invoke onCompleteOfLastErrorRendered.
      // The ReactErrorRenderer also has a pfs button.
      if (!this.reactErrRenderer) {
        this.reactErrRenderer = new OnCompleteReactErrorRenderer(this.onCompleteOfLastErrorRendered.bind(this));
        // Do NOT bother resetting renderHandler to original value after app fully initialized as the
        // onCompleteOfLastErrorRenderer method will cause browser tab to reload (including logging out if user is authenticated)
        LogAndRenderErrorPipeline.pipeline.renderHandler = this.reactErrRenderer;
      }

      // Now report given error, which causes it to be debounced, logged ane rendered.
      LogAndRenderErrorPipeline.pipeline.reportError(err);
    } catch (errWhileDisplaying) {
      try {
        // At this point, can not get react error renderer up, so no PFS is available.
        // So LOG not needed, and just use safer rudimentary console.error
        console.error("Swallowed following error while trying to render application initialization errors: " + errWhileDisplaying);
      } finally {
        // But really care about the application initialization error - last gasp to try and put up something meaningful
        this.veryLastChanceErrorRenderer(err);
      }
    }

    // Do NOT throw otherwise Bootstrap component itself unmounts, and user doesn't see the ReactErrorRender dialog.
    // They just see the dreaded WSOD (White Screen of Death)
    // throw err;
  }

  /**
   * We're in a hosed state if this is ever invoked as fundamental services haven't even initialized.
   * As such, logs and renders (via 'safe' alert) the main error, and then logs out/redirects browser
   * @param err
   */
  veryLastChanceErrorRenderer(err) {
    if (this.invokedVeryLastChanceErrorRenderer) {
      console.error("Skipping redundant invocation of invokedVeryLastChanceErrorRenderer");
      return;
    }
    this.invokedVeryLastChanceErrorRenderer = true;
    try {
      LogAndRenderErrorPipeline.pipeline.logError(err); // debounces, logs error
      // Rare case: failed so early that even AbWebServices aren't initialized (typically first thing an
      // app initializes).  Likely nothing works if AbWebServices aren't even initialized.
      // No rpc support, no pfs, no i18n, no image registry, ... So resort to raw 'alert' output.
      alert("Application initialization failed with following exception: \n" + err + "\n\nClick OK to reload the application"); // blocking
    } finally {
      this.onCompleteOfLastErrorRendered();
    }
  }
  onCompleteOfLastErrorRendered() {
    // If authenticated user, then logout
    if (AbWebServices.isInitialized() && AbWebServices.sessionData.currentUser && !AbWebServices.sessionData.currentUser.isAnonymous) {
      // Logout ALWAYS causes redirect to logout url (no chance of it throwing an exception)
      AbWebServices.authenticationService.logout();
    } else {
      this.redirectToRootAppUrl();
    }
  }
  redirectToRootAppUrl() {
    // brute force redirect to root app url (everything up to first '?' or '#')
    const currUrl = BrowserUtils.getLocationHref();
    const appUrl = currUrl.split(/[?#]/, 1);
    const rootUrl = appUrl[0];
    BrowserUtils.setLocationHref(rootUrl);
  }

  /**
   *  @returns true iff AbReactServices has been successfully initialized
   */
  isReactErrorRendererUsable() {
    // Note we initially tried to use the react error renderer if only AbWebServices were initialized,
    // but got into issue where needed to make all i18n resource usages 'safe' by catching exceptions around when
    // the abreact bundle had not yet loaded.  This was brittle as any other machinery transitively used by the
    // react error dialog also needed to be hardened against abreact resource lookup failures, and it frequently broke
    // (e.g., the popup handler adds a nice tooltip msg backed by an AbReactResource lookup, and then the
    // ReactErrorRenderer rendering fails). Note that abreact services are minimal, should (almost?) always succeed when
    // abwebservices successfully initializes.  Thus not worth playing slippery slope game.
    return AbReactServices.isInitialized();
  }
}

/**
 * Defines ReactErrorRenderer that invokes onComplete callback invoked when react error renderer is closed
 * (after it displays the buffered errors).
 */
class OnCompleteReactErrorRenderer {
  constructor(onComplete) {
    _defineProperty(this, "reactErrRenderer", new ReactErrorRenderer());
    this.onComplete = onComplete;
  }
  handleErrorContext(errCtx) {
    this.reactErrRenderer.handleErrorContext(errCtx, this.onComplete);
  }
}

/**
 * Activate this functionality with
 * window.LOG_CLICK_TEST_IDS=true
 * in either the browser console, or within a test.
 * Doing so will log data-test-id-based selectors to the console on every click.
 *
 * Benefits - This produces a selector consisting only of data-attributes, including
 * all ancestor elements which have data-attributes.  Elements without data-attributes
 * are not used to build the selector.
 * Tradeoff - this doesn't attempt to produce a unique selector.
 * Mitigation- when selectors are not unique, this can help point out controls and layout elements
 * in a UI that would benefit from having data-test-id attributes added.
 *
 * This is meant as a supplement, not a replacement, to the test recorder chrome plugin which Derek Benson maintains.
 * - The recorder does attempt to produce a unique selector by including other selector syntax such as
 * css classnames and other selector syntax.
 * - The recorder plugin is not available within the cypress test runner UI.
 * - The recorder plugin captures other interactions such as typing and drag drops
 *
 * @authors Jeff Hoel, Derek Benson, Andy Armstrong
 */
export function installLoggerClickTestIds() {
  /* eslint-disable abinitio/check-listeners */
  document.body.addEventListener("click", e => {
    if (window.LOG_CLICK_TEST_IDS) {
      // on every click, log a selector made of [data-test-id='foo'] pieces to the console
      console.log(`"${dataTestSelector(e.target)}"`); //`
    }
  }, {
    capture: true
  } // https://reactjs.org/blog/2020/08/10/react-v17-rc.html#fixing-potential-issues
  );

  //(window as any).dataTestSelector = dataTestSelector; // to call dataTestSelector($0) from the debug console

  function dataTestSelector(elem) {
    if (elem.parentElement == null) {
      return "";
    }
    let ret = dataTestSelector(elem.parentElement);
    // get all data- attributes
    const dataAttributes = elem.getAttributeNames().filter(name => name.indexOf("data-") === 0);
    // sort by preference
    dataAttributes.sort((a, b) => {
      if (a !== b) {
        // best
        if (a === "data-test-id") return -1;
        if (b === "data-test-id") return 1;
        // next best
        if (a === "data-ab-image-key") return -1;
        if (b === "data-ab-image-key") return 1;
        // good enough
        if (a === "data-test-component") return -1;
        if (b === "data-test-component") return 1;
        // any other data-foo attributes, just prefer by alphabetical order
        return a < b ? -1 : 1;
      }
      return 0;
    });
    let append = dataAttributes.length > 0 ? ` [${dataAttributes[0]}='${elem.getAttribute(dataAttributes[0])}']` : "";
    ret += append;
    return ret;
  }
}

/////////   BootstrapAppLoaderFn Helper Functions ///////

// Restrict keys of T to those that are of AllowedT type

// strongly type function to import BootstrapAppLoaderFn
async function loadBootstrapAppLoaderFn(importModuleFn, exportName) {
  return ModuleLoaderUtils.loadExport(importModuleFn, exportName);
}

// create BootstrapAppLoaderFn that will dynamically load BootstrapAppFn and invoke it
export function createBootstrapAppLoaderFn(importModuleFn, exportName) {
  return async alProps => {
    const bootstrapAppLoaderFn = await loadBootstrapAppLoaderFn(importModuleFn, exportName);
    alProps.profileStartup("Finished importing BootstrapAppLoaderFn: " + exportName.toString());
    return bootstrapAppLoaderFn(alProps);
  };
}
//# sourceMappingURL=BootstrapApp.js.map
